import { useEffect, useState } from 'react';

import { TRecursiveNonNullable } from '@api/Helper.types';
import {
    useGetCustomerState,
    usePutStateMutation
} from '@api/lendingAPI/lapiHooks';
import { yupResolver } from '@hookform/resolvers/yup';
import { tracking } from '@iwoca/frontend-tracking-library';
import { IconAlertTriangle } from '@iwoca/icons';
import {
    GetProductOffersResponse,
    GetStateByStateKeyResponse,
    PutStateRequestBody
} from '@iwoca/lapi-client/edge';
import { AlertBox, Button, Input, Spinner } from '@iwoca/orion';
import { getCurrentPathname } from '@iwRouter/LocationHelper';
import { produce } from 'immer';
import { useForm } from 'react-hook-form';
import * as yup from 'yup';

import AgedDebtorReport from './requirementTypes/AgedDebtorReport';
import BusinessPlan from './requirementTypes/BusinessPlan';
import CompanyAccounts from './requirementTypes/CompanyAccounts';
import ManagementAccounts from './requirementTypes/ManagementAccounts';
import VATReturns from './requirementTypes/VATReturns';
import styles from './TradingHistory.module.css';
import { useStateKey } from '../../../iwQueryClient/useStateKey';

type TCustomerStateForm = {
    website?: string;
};

type TGetCustomerStateResponseData =
    TRecursiveNonNullable<GetStateByStateKeyResponse> & {
        data: {
            ui?: Record<string, unknown>;
        };
    };

export type TGetProductOffersResponseData =
    TRecursiveNonNullable<GetProductOffersResponse>['data'];

const TradingHistory = ({
    optionalDocs
}: {
    optionalDocs: TGetProductOffersResponseData['products'][number]['data_requirements'];
}) => {
    const [editWebsite, setEditWebsite] = useState(false);
    const [loadingWebsite, setLoadingWebsite] = useState(false);
    const stateKey = useStateKey();
    const { data: stateData, refetch } = useGetCustomerState(stateKey);
    const {
        mutatePutState,
        isLoading: putStateLoading,
        isError
    } = usePutStateMutation(stateKey);

    const DOCUMENT_REQUIREMENTS: { [key: string]: React.ElementType } = {
        company_accounts: CompanyAccounts,
        management_accounts: ManagementAccounts,
        business_plan: BusinessPlan,
        aged_debtor_report: AgedDebtorReport,
        vat_return: VATReturns
    };

    const {
        register,
        handleSubmit,
        setValue,
        formState: { errors }
    } = useForm({
        resolver: yupResolver(
            yup.object({
                website: yup
                    .string()
                    .matches(
                        /^$|((http|https):\/\/)?[a-zA-Z0-9./?:@\-_=#]+\.([a-zA-Z0-9&./?:@\-_=#])*/,
                        'Please enter a valid website'
                    )
            })
        )
    });

    useEffect(() => {
        if (!stateData) return;

        if (stateData?.application?.company?.website) {
            setValue('website', stateData.application.company.website);
        }
    }, [setValue, stateData]);

    const renderDocumentRequirements = (
        requirements: TGetProductOffersResponseData['products'][number]['data_requirements']
    ) => {
        return requirements.map((requirement) => {
            if (!DOCUMENT_REQUIREMENTS[requirement.type]) {
                return null;
            }

            const RequirementDetails = DOCUMENT_REQUIREMENTS[requirement.type];
            return (
                <RequirementDetails
                    key={requirement.type}
                    isOptional={requirement.is_optional}
                    quantifier={requirement.quantifier}
                    resolutionMethods={requirement.resolution_methods}
                    status={requirement.status}
                />
            );
        });
    };

    const denormaliseWebsite = (website?: string) => {
        if (!website) {
            return undefined;
        }

        const websiteWithoutWhitespace = website.replaceAll(/\s/g, '');

        const websiteIncludesHttp =
            websiteWithoutWhitespace.match(/(http)(?!s)|(https)/g);

        return websiteIncludesHttp
            ? websiteWithoutWhitespace
            : `https://${websiteWithoutWhitespace}`;
    };

    const serialisePutStateBody = (
        value: TCustomerStateForm,
        existingStateData: TGetCustomerStateResponseData['data']
    ): PutStateRequestBody => {
        const website = denormaliseWebsite(value.website);

        const updatedStateData = produce(existingStateData, (draft) => {
            if (website) {
                draft.application.company.website = website;
            } else {
                // @ts-ignore
                delete draft.application.company.website;
            }
        });

        return {
            data: updatedStateData
        };
    };

    const handleWebsiteCTA = () => {
        if (!editWebsite && !!stateData?.application?.company?.website) {
            return (
                // Native button because form issue with type="button" not on the same element and triggered submit handler
                <button
                    className="flex h-3xl min-w-[85px] shrink-0 items-center justify-center gap-s rounded-s border border-secondary-80 bg-gradient-to-b from-[#DDE8EE] to-[#C4D8E3] px-l py-s text-m font-bold text-primary-40 transition-opacity hover:opacity-85"
                    type="button"
                    onClick={() => {
                        tracking.ampli.ctaClick({
                            'CTA text': 'Edit',
                            'CTA type': 'button',
                            pageName: 'Application pending view',
                            URL: getCurrentPathname()
                        });

                        return setEditWebsite(true);
                    }}
                >
                    Edit
                </button>
            );
        }

        return (
            <Button
                variant="secondary"
                className="min-w-[85px]"
                disabled={putStateLoading}
                type="submit"
                onClick={() => {
                    tracking.ampli.ctaClick({
                        'CTA text': 'Save',
                        'CTA type': 'button',
                        pageName: 'Application pending view',
                        URL: getCurrentPathname()
                    });
                }}
            >
                {loadingWebsite ? (
                    <>
                        <span>Saving</span> <Spinner />
                    </>
                ) : (
                    'Save'
                )}
            </Button>
        );
    };

    const onSubmit = async (value: { website?: string }) => {
        try {
            setLoadingWebsite(true);
            await mutatePutState({
                body: serialisePutStateBody(
                    value,
                    stateData as TGetCustomerStateResponseData['data']
                )
            });
            await refetch();
            setEditWebsite(false);
            setLoadingWebsite(false);
        } catch {
            setLoadingWebsite(false);
        }
    };

    return (
        <>
            <h4 className={styles.title}>Provide extra data</h4>
            <p
                className={styles.description}
                data-testid="optional-doc-description"
            >
                This isn't mandatory, but including more information would help
                us get a clearer picture of your business.
            </p>
            <form
                onSubmit={handleSubmit(onSubmit)}
                className="mb-s max-xs:w-full max-xs:max-w-[280px]"
            >
                <Input.Label
                    htmlFor="website"
                    className="font-medium mb-l text-l"
                >
                    Website
                </Input.Label>
                <div className="flex max-xs:flex-col min-sm:items-center">
                    <Input.Control
                        {...register('website')}
                        data-dd-privacy="mask"
                        className="fs-exclude mr-s font-reg disabled:text-structure-40 min-sm:min-w-[280px]"
                        placeholder="https://www.your-website.com"
                        id="website"
                        disabled={
                            (!editWebsite &&
                                !!stateData?.application?.company?.website) ||
                            putStateLoading
                        }
                    />
                    <div className="max-xs:mt-l">{handleWebsiteCTA()}</div>
                </div>
                {errors.website && (
                    <p className="mb-0 mt-m flex items-center text-s text-error">
                        <IconAlertTriangle className="mr-s fill-error" />
                        {errors.website?.message}
                    </p>
                )}
                {isError && (
                    <AlertBox
                        data-testid="network-error"
                        variant="failure"
                        className="mt-m flex items-center"
                    >
                        <IconAlertTriangle className="mr-s" />
                        <div className="text-xs">
                            <strong>Please try again</strong> - We encountered
                            an error while saving your website.
                        </div>
                    </AlertBox>
                )}
            </form>
            {renderDocumentRequirements(
                optionalDocs as TGetProductOffersResponseData['products'][number]['data_requirements']
            )}
        </>
    );
};

// eslint-disable-next-line import/no-default-export
export default TradingHistory;
